import { useCallback, useEffect, useRef, useState } from "react";
import type { BillingAccountResponse, BillingPlansResponse } from "@/types";

export type LoadingState = "idle" | "loading" | "error" | "success";

export interface CreditState {
  balance: number;
  lastUpdated: string | null;
  creditUnitChars: number;
}

export interface AccountState extends BillingAccountResponse {
  fetchedAt: number;
}

export interface BillingState {
  credits: CreditState | null;
  creditsLoadingState: LoadingState;
  creditsLastFetched: number;
  creditsError: string | null;
  account: AccountState | null;
  accountLoadingState: LoadingState;
  accountLastFetched: number;
  accountError: string | null;
  plans: BillingPlansResponse | null;
  plansLoadingState: LoadingState;
  plansLastFetched: number;
  plansError: string | null;
}

export interface BillingActions {
  refreshCredits: (force?: boolean) => Promise<void>;
  refreshAccount: (force?: boolean) => Promise<void>;
  refreshPlans: (force?: boolean) => Promise<void>;
  refreshAll: (force?: boolean) => Promise<void>;
  invalidateCredits: () => void;
  invalidateAccount: () => void;
  invalidatePlans: () => void;
  invalidateAll: () => void;
}

export interface BillingContextValue extends BillingState, BillingActions {}

export const CACHE_TTL = {
  credits: 30 * 1000,
  account: 5 * 60 * 1000,
  plans: 10 * 60 * 1000,
};

type ErrorLike = {
  message?: string;
  code?: string;
  status?: number;
};

function toErrorLike(err: unknown): ErrorLike | null {
  if (typeof err !== "object" || err === null) return null;
  const e = err as Record<string, unknown>;
  return {
    message: typeof e.message === "string" ? e.message : undefined,
    code: typeof e.code === "string" ? e.code : undefined,
    status: typeof e.status === "number" ? e.status : undefined,
  };
}

export function isAuthNotReadyError(err: unknown): boolean {
  const e = toErrorLike(err);
  return e?.code === "AUTH_NO_TOKEN" || e?.message === "authentication_required";
}

export function isBillingAccountNotAvailableError(err: unknown): boolean {
  const e = toErrorLike(err);
  return e?.status === 503 || e?.status === 404;
}

type CachedFetchState<TState> = {
  data: TState | null;
  loadingState: LoadingState;
  lastFetched: number;
  error: string | null;
  refresh: (force?: boolean) => Promise<void>;
  invalidate: () => void;
};

export function useTtlCachedFetch<TApi, TState>(params: {
  ttlMs: number;
  fetcher: () => Promise<TApi>;
  mapData: (data: TApi, fetchedAt: number) => TState;
  errorFallbackMessage: string;
  suppressErrorIf?: (err: unknown) => boolean;
  treatErrorAsSuccessIf?: (err: unknown) => boolean;
}): CachedFetchState<TState> {
  const {
    ttlMs,
    fetcher,
    mapData,
    errorFallbackMessage,
    suppressErrorIf,
    treatErrorAsSuccessIf,
  } = params;

  const [data, setData] = useState<TState | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>("idle");
  const [lastFetched, setLastFetched] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const lastFetchedRef = useRef(lastFetched);
  const loadingStateRef = useRef(loadingState);
  // Synchronous in-flight guard — set before fetch, cleared in finally.
  // This avoids the race where two refresh() calls in the same effect flush
  // both read the state-derived guard as idle (refs sync one commit later).
  const inFlightRef = useRef(false);
  // render. Without this, callers passing inline arrows (e.g. `() => client.get()`)
  // produce a new `fetcher` per render, which would make `refresh` unstable and
  // cause downstream useEffect deps to refire — turning a single 403 into an
  // infinite re-fetch loop on the error path.
  const fetcherRef = useRef(fetcher);
  const mapDataRef = useRef(mapData);
  const errorFallbackMessageRef = useRef(errorFallbackMessage);
  const suppressErrorIfRef = useRef(suppressErrorIf);
  const treatErrorAsSuccessIfRef = useRef(treatErrorAsSuccessIf);

  useEffect(() => {
    fetcherRef.current = fetcher;
    mapDataRef.current = mapData;
    errorFallbackMessageRef.current = errorFallbackMessage;
    suppressErrorIfRef.current = suppressErrorIf;
    treatErrorAsSuccessIfRef.current = treatErrorAsSuccessIf;
  });

  useEffect(() => {
    lastFetchedRef.current = lastFetched;
  }, [lastFetched]);

  useEffect(() => {
    loadingStateRef.current = loadingState;
  }, [loadingState]);

  const refresh = useCallback(async (force = false) => {
    const now = Date.now();
    const isCacheValid = lastFetchedRef.current > 0 &&
      (now - lastFetchedRef.current) < ttlMs;

    // `force` bypasses the TTL gate (e.g. returning from Stripe checkout, where
    // the server-side grant just changed and the cached copy is known stale).
    // The synchronous in-flight guard avoids duplicate concurrent fetches.
    if ((!force && isCacheValid) || inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    setLoadingState("loading");
    setError(null);

    try {
      const apiData = await fetcherRef.current();

      const fetchedAt = Date.now();
      setData(mapDataRef.current(apiData, fetchedAt));
      setLoadingState("success");
      setLastFetched(fetchedAt);
    } catch (err) {
      if (suppressErrorIfRef.current?.(err)) {
        setLoadingState("idle");
        inFlightRef.current = false;
        return;
      }

      if (treatErrorAsSuccessIfRef.current?.(err)) {
        setLoadingState("success");
        setLastFetched(Date.now());
        inFlightRef.current = false;
        return;
      }

      const message = err instanceof Error
        ? err.message
        : errorFallbackMessageRef.current;
      setError(message);
      // Mark the error as "fetched" so the TTL gate suppresses immediate
      // refetches; without this, an error keeps `lastFetched === 0`, the cache
      // is treated as invalid forever, and any re-render re-hits the endpoint.
      setLastFetched(Date.now());
      setLoadingState("error");
    } finally {
      inFlightRef.current = false;
    }
  }, [ttlMs]);

  const invalidate = useCallback(() => {
    setLastFetched(0);
  }, []);

  return {
    data,
    loadingState,
    lastFetched,
    error,
    refresh,
    invalidate,
  };
}

export function mapCredits(
  data: { balance: number; lastUpdated: string | null; creditUnitChars?: number },
  _fetchedAt: number,
): CreditState {
  return {
    balance: data.balance,
    lastUpdated: data.lastUpdated,
    creditUnitChars: data.creditUnitChars ?? 1000,
  };
}

export function mapAccount(
  data: BillingAccountResponse,
  fetchedAt: number,
): AccountState {
  return { ...data, fetchedAt };
}

export function mapPlans(
  data: BillingPlansResponse,
  _fetchedAt: number,
): BillingPlansResponse {
  return data;
}
